<template>
  <content-subpage
    class="app-detail-content-subpage"
    :visible="visible"
    :titles="titles"
    :has-parent-subpage="hasParentSubpage"
    :show-content-title="false"
    :back-label="backLabel"
    @content-subpage-hidden="actionHidden(handleHidden())"
    @content-subpage-back="actionBack"
  >
    <template #title-buttons>
      <star-resource-button
        :resource-uuid="params.uuid"
        resource-type="APP"
        @resource-unstarred="handleResourceUnstarred"
      />
    </template>
    <template #main-content>
      <inline-loader v-if="isLoading" />
      <div v-else>
        <match-indicator v-if="matchAppName" />
        <div class="info-header">
          <ellipsis-text class="app-name" :text="name" />
          <cv-link v-if="canLaunchApp" class="app-link" @click="handleLaunchingApp">
            <svg-icon class="view-ext" name="ExternalLink32" kind="action" />
          </cv-link>
        </div>

        <cv-form>
          <dl class="field-set thumbnail">
            <dd v-if="imageUrl" class="field-value">
              <img :src="imageUrl" />
            </dd>
          </dl>

          <match-indicator v-if="matchDescription" />
          <dl class="field-set description">
            <dd v-if="description" class="field-value">{{ description }}</dd>
          </dl>
        </cv-form>
      </div>
    </template>

    <template #sub-content>
      <inline-loader v-if="isLoading" />
      <cv-form v-else>
        <dl class="field-set contact">
          <dt class="field-label">{{ t("app_subpage_contact_no") }}</dt>
          <dd v-if="contactNo" class="field-value">{{ contactNo }}</dd>
        </dl>
        <dl class="field-set email">
          <dt class="field-label">{{ t("app_subpage_email") }}</dt>
          <dd v-if="email" class="field-value">{{ email }}</dd>
        </dl>
        <match-indicator v-if="matchKeyword" />
        <dl class="field-set keyword">
          <dt class="field-label">{{ t("app_subpage_keyword_tags") }}</dt>
          <dd class="field-value">
            <keywords v-if="showKeywords" :tags="app.keywords" />
            <span v-else>-</span>
          </dd>
        </dl>
      </cv-form>
    </template>

    <!-- Choose button -->
    <template v-if="hasParentSubpage" #action-button>
      <cv-button class="choose-selected-btn" @click="handleChoose">
        <svg-icon name="Plus24" kind="action" />
        <span>{{ t("embed_website_choose_button_label") }}</span>
      </cv-button>
    </template>
  </content-subpage>
</template>

<script setup lang="ts">
import AppListApi from "@apis/app.list.api";
import Keywords from "@plugins/keywords/components/Keywords";
import { isEmpty } from "@utils/var.check";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";
import { useContentSubpage } from "@plugins/content-subpage/composables/content-subpage.ts";
import { useErrorHandler } from "@plugins/common/composables/error.ts";
import { useRouting } from "@plugins/common/composables/routing.ts";
import { ref, computed, onMounted } from "vue";
import userActivityTrackEvent from "@plugins/user-activity-track/service/user.activity.track.event.ts";

interface Props {
  params?: object | null;
}

const props = withDefaults(defineProps<Props>(), {
  params: null,
});

const emit = defineEmits<{
  onAppSelected: [];
  resourceUnstarred: [];
}>();

const { t } = useI18n();

const app = ref(null);
const isLoading = ref(true);
const resourceUnstarred = ref(false);

const router = useRouter();
const { visible, titles, hasParentSubpage, actionBack, actionHidden, defaultExpose } =
  useContentSubpage(props.params);
const { passiveApiFailHandler } = useErrorHandler();
const { urlReady, handleRouteIntervalCheck } = useRouting();

const imageUrl = computed(() => {
  return !isEmpty(app.value.iconFileName) ? `${app.value.iconFileName}?t=${Date.now()}` : null;
});

const description = computed(() => {
  return app.value.description ? app.value.description : "-";
});

const contactNo = computed(() => {
  return app.value.helpdeskContactNo ? app.value.helpdeskContactNo : "-";
});

const email = computed(() => {
  return app.value.helpdeskEmail ? app.value.helpdeskEmail : "-";
});

const name = computed(() => {
  return app.value.name ? app.value.name : "-";
});

const showKeywords = computed(() => {
  return !isEmpty(app.value.keywords);
});

const parentSubpageName = ref(titles.value.at(-1) ?? "");

const backLabelSubject = computed(() => {
  // custom back label used in 'Embed App' in rich text editor.
  return props.params.customBackLabel ?? parentSubpageName.value;
});

const backLabel = computed(() => {
  return `${t("content_subpage_go_back_link")} ${backLabelSubject.value}`;
});

const canLaunchApp = computed(() => {
  if (!app.value.enabled) {
    return false;
  }

  return props.params.showExternalLink !== undefined
    ? props.params.showExternalLink
    : app.value.launchable;
});

const matchDescription = computed(() => {
  return isEmpty(props.params.highlights) ? null : props.params.highlights.description;
});

const matchKeyword = computed(() => {
  return isEmpty(props.params.highlights) ? null : props.params.highlights.keyword;
});

const matchAppName = computed(() => {
  return !isEmpty(props.params.highlights) && !isEmpty(props.params.highlights.name);
});

const handleLaunchingApp = () => {
  handleRouteIntervalCheck();
  AppListApi.fetchIndependentAppContext({
    //ok
    params: {
      appUuid: app.value.uuid,
      type: "LAUNCH_APP",
    },
  }).then((response) => {
    if (response.code === "ok") {
      const result = response.data;
      urlReady.value = result.launchUrl;

      // Track app launch event
      userActivityTrackEvent.launchApp({
        appUuid: app.value.uuid,
        appName: app.value.name ?? "",
      });
    } else {
      const routeData = router.resolve({
        name: "Forbidden",
      });
      urlReady.value = routeData.href;
    }
  }, passiveApiFailHandler);
};

const handleChoose = () => {
  emit("onAppSelected");
};

const handleResourceUnstarred = (unstarred: boolean) => {
  resourceUnstarred.value = unstarred;
};

const handleHidden = () => {
  if (resourceUnstarred.value) {
    emit("resourceUnstarred");
  }
};

onMounted(async () => {
  const res = await AppListApi.fetchAppMetadata(props.params?.uuid as string);
  if (res.code !== "ok") return passiveApiFailHandler();
  app.value = res.data;
  titles.value.push(app.value.name, t("search_resource_detail_app_details_label"));
  isLoading.value = false;
});

defineExpose(defaultExpose);
</script>

<style lang="scss">
@use "@styles/themes/sls-theme/tokens/colors" as *;
@use "@styles/abstracts/mixins" as *;
@use "@styles/themes/sls-theme/tokens/spacing" as *;
@use "@styles/themes/sls-theme/tokens/typo" as *;

.content-subpage.app-detail-content-subpage {
  .bx--modal-container {
    > .header {
      .breadcrumb {
        .title {
          max-width: 100%;
        }
      }
    }

    .thumbnail {
      .field-value {
        display: flex;
        justify-content: center;
        background-color: $ui-03;
      }

      img {
        width: 128px;
        height: 128px;
        object-fit: cover;
      }
    }
  }

  .info-header {
    display: flex;
    align-items: center;
  }

  .app-name {
    @include carbon--type-style("productive-heading-02", true);
    @include line-clamp(2);
  }

  .app-link {
    margin-left: $spacing-03;

    .sls-icon {
      [fill] {
        fill: $interactive-01;
      }
    }
  }
}
</style>
